import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { calendarKeys } from '@/features/schedules/api'
import { displayKeys } from '@/features/displays/api'
import { api, unwrap } from '@/lib/api'
import type { DataEnvelope, EmergencyBroadcast } from '@/types/api'
import type { EmergencyInput } from './lib/emergency'

export const emergencyKeys = {
  all: ['emergency-broadcasts'] as const,
  list: () => [...emergencyKeys.all, 'list'] as const,
}

/** Latest 100 broadcasts. `poll` refreshes every 30 s (shell banner, emergency page). */
export function useEmergencyBroadcasts({ enabled = true, poll = false }: { enabled?: boolean; poll?: boolean } = {}) {
  return useQuery({
    queryKey: emergencyKeys.list(),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<EmergencyBroadcast[]>>('/emergency-broadcasts', { signal })),
    enabled,
    refetchInterval: poll ? 30_000 : false,
  })
}

function invalidateEmergency(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: emergencyKeys.all })
  void qc.invalidateQueries({ queryKey: calendarKeys.all })
  void qc.invalidateQueries({ queryKey: displayKeys.details() })
}

export function useStartBroadcast() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: EmergencyInput) => unwrap(api.post<DataEnvelope<EmergencyBroadcast>>('/emergency-broadcasts', input)),
    onSuccess: (b) => {
      qc.setQueryData<EmergencyBroadcast[]>(emergencyKeys.list(), (list) => (list ? [b, ...list.filter((x) => x.id !== b.id)] : list))
      invalidateEmergency(qc)
    },
  })
}

export function useEndBroadcast() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.post<DataEnvelope<EmergencyBroadcast>>(`/emergency-broadcasts/${id}/end`)),
    onSuccess: (b) => {
      qc.setQueryData<EmergencyBroadcast[]>(emergencyKeys.list(), (list) => list?.map((x) => (x.id === b.id ? b : x)))
      invalidateEmergency(qc)
    },
    // `409 broadcast-ended`: someone else ended it (or it expired) — refresh the list either way.
    onError: () => invalidateEmergency(qc),
  })
}
